import {
  faArrowDown,
  faArrowUp,
  faSearch,
  faTimes,
  faTrash,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  memo,
  useCallback,
  useMemo,
  useState,
  type ChangeEvent,
} from 'react'
import { useTheme } from '../../../../theme/hooks'
import { formatDate } from '../lidarModalUtils'
import { Pagination } from './Pagination'
import type { Raster, SortConfig, SortKey } from './types'

type Props = {
  rasters: Raster[]
  onDelete: (raster: Raster) => void
  canDelete: boolean
}

const ITEMS_PER_PAGE = 2

type SortArrowProps = {
  active: boolean
  direction: 'ascending' | 'descending'
  target: 'ascending' | 'descending'
  highlightColor: string
  baseColor: string
}

const SortArrowBase = ({
  active,
  direction,
  target,
  highlightColor,
  baseColor,
}: SortArrowProps) => (
  <FontAwesomeIcon
    icon={target === 'ascending' ? faArrowUp : faArrowDown}
    className="h-2 w-2"
    style={{
      color: active && direction === target ? highlightColor : baseColor,
    }}
  />
)
const SortArrow = memo(SortArrowBase)

const RastersTableBase = ({ rasters, onDelete, canDelete }: Props) => {
  const { theme } = useTheme()
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'ascending',
  })
  const [searchVisible, setSearchVisible] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [activePage, setActivePage] = useState(1)

  const toggleSearch = useCallback(() => {
    setSearchVisible(prev => !prev)
  }, [])

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value)
      setActivePage(1)
    },
    [],
  )

  const sortData = useCallback((key: SortKey) => {
    setSortConfig(prev => ({
      key,
      direction:
        prev.key === key && prev.direction === 'ascending'
          ? 'descending'
          : 'ascending',
    }))
  }, [])

  const handlePageChange = useCallback((page: number) => {
    setActivePage(page)
  }, [])

  const filteredRasters = useMemo(() => {
    const lower = searchTerm.toLowerCase()
    if (!lower) return rasters
    return rasters.filter(raster => {
      const matchesName = raster.filename?.toLowerCase().includes(lower)
      const matchesDate = raster.upload_date
        ? formatDate(raster.upload_date).toLowerCase().includes(lower)
        : false
      return Boolean(matchesName || matchesDate)
    })
  }, [rasters, searchTerm])

  const sortedRasters = useMemo(() => {
    if (!sortConfig.key) return filteredRasters
    const dir = sortConfig.direction === 'ascending' ? 1 : -1
    const key = sortConfig.key
    return [...filteredRasters].sort((a, b) =>
      ((a[key] ?? '') as string).localeCompare(
        (b[key] ?? '') as string,
      ) * dir,
    )
  }, [filteredRasters, sortConfig])

  const totalPages = Math.ceil(sortedRasters.length / ITEMS_PER_PAGE)
  const visibleRasters = useMemo(
    () =>
      sortedRasters.slice(
        (activePage - 1) * ITEMS_PER_PAGE,
        activePage * ITEMS_PER_PAGE,
      ),
    [sortedRasters, activePage],
  )

  const renderSortableHeader = (label: string, key: SortKey) => {
    const active = sortConfig.key === key
    return (
      <th
        className="cursor-pointer px-4 py-3 text-left text-sm font-medium transition-colors"
        style={{ color: theme.table.textHeader }}
        onMouseEnter={e => {
          e.currentTarget.style.color = theme.text.link
        }}
        onMouseLeave={e => {
          e.currentTarget.style.color = theme.table.textHeader
        }}
        onClick={() => sortData(key)}
      >
        <div className="flex items-center gap-2">
          {label}
          <div className="flex flex-col">
            <SortArrow
              active={active}
              direction={sortConfig.direction}
              target="ascending"
              highlightColor={theme.text.link}
              baseColor={theme.text.tertiary}
            />
            <SortArrow
              active={active}
              direction={sortConfig.direction}
              target="descending"
              highlightColor={theme.text.link}
              baseColor={theme.text.tertiary}
            />
          </div>
        </div>
      </th>
    )
  }

  return (
    <div
      className="mt-8 border-t pb-6 pt-6"
      style={{ borderTopColor: theme.section.border }}
    >
      <div className="mb-4 flex items-center justify-between">
        <h3
          className="text-lg font-semibold"
          style={{ color: theme.text.primary }}
        >
          Mes Couches
        </h3>
        <button
          className="flex items-center justify-center rounded-full p-2 transition-colors"
          style={{ color: theme.text.tertiary }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = theme.background.hover
            e.currentTarget.style.color = theme.text.primary
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent'
            e.currentTarget.style.color = theme.text.tertiary
          }}
          onClick={toggleSearch}
          title="Rechercher"
        >
          <FontAwesomeIcon
            icon={searchVisible ? faTimes : faSearch}
            className="h-4 w-4"
          />
        </button>
      </div>

      {searchVisible && (
        <div className="mb-4">
          <input
            type="text"
            placeholder="Rechercher par nom ou date..."
            className="w-full rounded-md border px-3 py-2 text-sm focus:outline-none"
            style={{
              borderColor: theme.input.border,
              backgroundColor: theme.input.bg,
              color: theme.input.text,
            }}
            onFocus={e => {
              e.currentTarget.style.borderColor = theme.input.borderFocus
              e.currentTarget.style.backgroundColor = theme.input.bgFocus
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = theme.input.border
              e.currentTarget.style.backgroundColor = theme.input.bg
            }}
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>
      )}

      <div
        className="overflow-hidden rounded-lg border"
        style={{
          borderColor: theme.section.border,
          boxShadow: theme.section.shadow,
        }}
      >
        <table className="w-full">
          <thead style={{ backgroundColor: theme.table.bgHeader }}>
            <tr>
              {renderSortableHeader('Nom du fichier', 'filename')}
              {renderSortableHeader('Date', 'upload_date')}
              <th
                className="px-4 py-3 text-left text-sm font-medium"
                style={{ color: theme.table.textHeader }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody
            className="divide-y"
            style={{
              backgroundColor: theme.table.bg,
              borderColor: theme.table.border,
            }}
          >
            {visibleRasters.map(raster => (
              <tr
                key={raster.id}
                className="transition-colors"
                style={{ borderColor: theme.table.border }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = theme.table.bgRowHover
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = theme.table.bgRow
                }}
              >
                <td
                  className="px-4 py-3 text-sm"
                  style={{ color: theme.table.text }}
                >
                  {raster.filename}
                </td>
                <td
                  className="px-4 py-3 text-sm"
                  style={{ color: theme.text.secondary }}
                >
                  {formatDate(raster.upload_date)}
                </td>
                <td className="px-4 py-3">
                  {canDelete && (
                    <button
                      onClick={() => onDelete(raster)}
                      className="flex items-center justify-center rounded-full p-1.5 transition-colors"
                      style={{ color: theme.status.error.icon }}
                      onMouseEnter={e => {
                        e.currentTarget.style.backgroundColor =
                          theme.status.error.bg
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.backgroundColor = 'transparent'
                      }}
                      title="Supprimer"
                    >
                      <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={activePage}
        totalPages={totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  )
}

export const RastersTable = memo(RastersTableBase)
